import ReactECharts from 'echarts-for-react';

/**
 * BotCareerPathChart_2 — "minimal editorial" variant of BotCareerPathChart (blog03).
 *
 * Same data, same chart family (two-series line over a categorical lifecycle).
 * Direction: opaque white card, generous whitespace, thin strokes, muted slate
 * palette with a single teal accent, small typography, no gradients/glows/shadows.
 * Quiet magazine-infographic feel.
 */

const EDITORIAL = {
  teal: '#0AB3BF', // single accent: autonomy
  slateLight: '#94A3B8', // review series + axis labels
  slateMid: '#64748B', // axis titles, legend, caption-adjacent text
  surface: '#FFFFFF',
};

const STAGES = ['Hired', 'Probation', 'Reviewed', 'Semi-Auto', 'Full-Auto'];
// Autonomy (what the bot does on its own) and Review (what a human still gates).
const AUTONOMY = [0, 10, 30, 60, 90];
const REVIEW = [100, 90, 70, 40, 10];

/** ONE shared percentage formatter — tooltip, axis ticks, and point labels all use it. */
const formatPct = (v: number) => `${Math.round(v)}%`;

export const BotCareerPathChart_2 = ({ height = 340 }: { height?: number }) => {
  const option = {
    backgroundColor: EDITORIAL.surface,
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#FFFFFF',
      borderColor: '#E2E8F0',
      textStyle: { color: EDITORIAL.slateMid, fontSize: 12 },
      formatter: (params: { seriesName: string; value: number; dataIndex: number; marker?: string }[]) => {
        const i = params[0].dataIndex;
        return `${STAGES[i]}<br/>${params
          .map((p) => `${p.marker} ${p.seriesName}: <b>${formatPct(p.value)}</b>`)
          .join('<br/>')}`;
      },
    },
    legend: {
      top: 12,
      textStyle: { color: EDITORIAL.slateMid, fontSize: 12 },
      itemWidth: 18,
      itemHeight: 2,
      icon: 'rect',
      data: ['Autonomy (bot acts)', 'Human review (gates)'],
    },
    grid: { left: 56, right: 48, top: 56, bottom: 44 },
    xAxis: {
      type: 'category',
      data: STAGES,
      axisLine: { lineStyle: { color: '#CBD5E1', width: 1 } },
      axisLabel: { color: EDITORIAL.slateLight, fontSize: 11 },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      name: 'share of actions (%)',
      nameTextStyle: { color: EDITORIAL.slateMid, fontSize: 11 },
      axisLabel: { color: EDITORIAL.slateLight, fontSize: 11, formatter: (v: number) => formatPct(v) },
      splitLine: { lineStyle: { color: '#E2E8F0', width: 1 } },
    },
    series: [
      {
        name: 'Autonomy (bot acts)',
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        data: AUTONOMY,
        itemStyle: { color: EDITORIAL.teal },
        lineStyle: { color: EDITORIAL.teal, width: 2 },
        label: {
          show: true,
          position: 'top',
          color: EDITORIAL.teal,
          fontWeight: 600,
          fontSize: 11,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
      {
        name: 'Human review (gates)',
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        data: REVIEW,
        itemStyle: { color: EDITORIAL.slateLight },
        lineStyle: { color: EDITORIAL.slateLight, width: 2, type: 'dashed' },
        label: {
          show: true,
          position: 'bottom',
          color: EDITORIAL.slateMid,
          fontWeight: 600,
          fontSize: 11,
          formatter: (p: { value: number }) => formatPct(p.value),
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 bg-white p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 text-center">
        Figure 1. Autonomy is earned: bot autonomy rises from 0% at hire to 90% at full-auto while human review falls
        from 100% to 10% — review scales with trust but never disappears. Hover for values.
      </p>
    </div>
  );
};
